import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

const API_URL = '/api';
const CORRECT_PIN = '1988';
const SESSION_KEY = 'masuria_med_auth';

// ============================================
// PIN LOGIN SCREEN
// ============================================
function PinLoginScreen({ onLogin }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const inputRefs = useRef([]);

  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newPin = pin.split('');
    newPin[index] = value;
    const updatedPin = newPin.join('').slice(0, 4);
    setPin(updatedPin);
    setError('');

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when 4 digits entered
    if (updatedPin.length === 4) {
      setTimeout(() => verifyPin(updatedPin), 100);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const verifyPin = (pinToVerify) => {
    if (pinToVerify === CORRECT_PIN) {
      sessionStorage.setItem(SESSION_KEY, 'authenticated');
      onLogin();
    } else {
      setError('Nieprawidłowy PIN');
      setShake(true);
      setPin('');
      setTimeout(() => {
        setShake(false);
        inputRefs.current[0]?.focus();
      }, 500);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pin.length === 4) {
      verifyPin(pin);
    }
  };

  return (
    <div className="pin-login-overlay">
      <div className={`pin-login-card ${shake ? 'shake' : ''}`}>
        <div className="pin-login-logo">
          <div className="pin-logo-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <h1>Masuria Med</h1>
          <p>System kontroli jakości obsługi pacjenta</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="pin-label">Wprowadź PIN dostępu</div>
          <div className="pin-inputs">
            {[0, 1, 2, 3].map((index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={pin[index] || ''}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="pin-input"
                autoFocus={index === 0}
              />
            ))}
          </div>
          {error && <div className="pin-error">{error}</div>}
          <button type="submit" className="pin-submit" disabled={pin.length !== 4}>
            Zaloguj się
          </button>
        </form>
      </div>
    </div>
  );
}

// ============================================
// IKONY SVG
// ============================================
const Icons = {
  recordings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
      <line x1="12" y1="19" x2="12" y2="23"/>
      <line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
  ),
  stats: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="20" x2="18" y2="10"/>
      <line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  reports: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  sun: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  ),
  moon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  ),
  phone: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  ),
  phoneIn: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="16 2 16 8 22 8"/>
      <line x1="23" y1="1" x2="16" y2="8"/>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  ),
  phoneOut: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 7 23 1 17 1"/>
      <line x1="16" y1="8" x2="23" y2="1"/>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  play: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
  ),
  pause: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="4" width="4" height="16"/>
      <rect x="14" y="4" width="4" height="16"/>
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  alert: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  ),
  menu: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="3" y1="6" x2="21" y2="6"/>
      <line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  ),
  award: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="8" r="7"/>
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/>
    </svg>
  ),
  arrowLeft: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="19" y1="12" x2="5" y2="12"/>
      <polyline points="12 19 5 12 12 5"/>
    </svg>
  ),
  download: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
  trendUp: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
      <polyline points="17 6 23 6 23 12"/>
    </svg>
  ),
  trendDown: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
      <polyline points="17 18 23 18 23 12"/>
    </svg>
  ),
  sales: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  target: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="6"/>
      <circle cx="12" cy="12" r="2"/>
    </svg>
  ),
  tooth: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2C9.5 2 7 3.5 7 6c0 2 .5 4 1 6s1.5 6 2.5 8c.5 1 1 2 1.5 2s1-1 1.5-2c1-2 2-6 2.5-8s1-4 1-6c0-2.5-2.5-4-5-4z"/>
    </svg>
  ),
  sparkles: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"/>
      <path d="M5 19l1 3 1-3 3-1-3-1-1-3-1 3-3 1 3 1z"/>
      <path d="M19 12l1 2 1-2 2-1-2-1-1-2-1 2-2 1 2 1z"/>
    </svg>
  ),
  graduation: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 10l-10-6L2 10l10 6 10-6z"/>
      <path d="M6 12v5c0 2 2.5 3 6 3s6-1 6-3v-5"/>
      <line x1="22" y1="10" x2="22" y2="16"/>
    </svg>
  ),
  speakerA: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="8" r="4"/>
      <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/>
    </svg>
  ),
  speakerB: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  training: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
    </svg>
  ),
  quiz: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  lightbulb: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="9" y1="18" x2="15" y2="18"/>
      <line x1="10" y1="22" x2="14" y2="22"/>
      <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
    </svg>
  ),
  message: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  correct: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  wrong: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="15" y1="9" x2="9" y2="15"/>
      <line x1="9" y1="9" x2="15" y2="15"/>
    </svg>
  )
};

// ============================================
// CUSTOM DROPDOWN SELECT
// ============================================
function CustomSelect({ value, onChange, options, placeholder = 'Wybierz...' }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  return (
    <div className={`custom-select ${isOpen ? 'open' : ''}`} ref={dropdownRef}>
      <div className="custom-select-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span className={`custom-select-value ${!selectedOption ? 'placeholder' : ''}`}>
          {displayText}
        </span>
        <span className="custom-select-arrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </span>
      </div>
      {isOpen && (
        <div className="custom-select-dropdown">
          {options.map((option) => (
            <div
              key={option.value}
              className={`custom-select-option ${value === option.value ? 'selected' : ''}`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
              {value === option.value && (
                <span className="custom-select-check">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// FLOATING AUDIO PLAYER - Sticky na dole
// ============================================
function FloatingAudioPlayer({ recording, utterances, onClose, onTimeUpdate, currentTime, seekToTime }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [localCurrentTime, setLocalCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);

  // Auto-play when component mounts
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.play().catch(() => {});
    }
  }, [recording.recording_id]);

  // Handle external seek command
  useEffect(() => {
    if (seekToTime !== null && seekToTime !== undefined && audioRef.current) {
      audioRef.current.currentTime = seekToTime / 1000;
      audioRef.current.play().catch(() => {});
    }
  }, [seekToTime]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const time = audioRef.current.currentTime * 1000;
      setLocalCurrentTime(time);
      if (onTimeUpdate) onTimeUpdate(time);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration * 1000);
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    if (audioRef.current && duration) {
      audioRef.current.currentTime = (percentage * duration) / 1000;
    }
  };

  const skip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime += seconds;
    }
  };

  const changeSpeed = () => {
    const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % speeds.length;
    const newRate = speeds[nextIndex];
    setPlaybackRate(newRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
  };

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('pl-PL');
  };

  const formatTimeString = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  // Find current utterance for display
  const currentUtterance = utterances?.find(u =>
    localCurrentTime >= u.start && localCurrentTime < u.end
  );

  return (
    <div className="floating-player">
      <audio
        ref={audioRef}
        src={`${API_URL}/recordings/${recording.recording_id}/audio`}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      <div className="floating-player-info">
        <div className="floating-player-avatar">
          {recording.agent_id ? recording.agent_id.charAt(0).toUpperCase() : '?'}
        </div>
        <div className="floating-player-details">
          <div className="floating-player-agent">
            {recording.agent_id || 'Nieznany'}
          </div>
          <div className="floating-player-meta">
            {formatDate(recording.call_date)} {formatTimeString(recording.call_time)}
          </div>
        </div>
      </div>

      <div className="floating-player-controls">
        <button className="audio-btn skip" onClick={() => skip(-10)} title="Cofnij 10s">
          <span className="skip-text">-10</span>
        </button>

        <button className="audio-btn play" onClick={togglePlay}>
          {isPlaying ? Icons.pause : Icons.play}
        </button>

        <button className="audio-btn skip" onClick={() => skip(10)} title="Przewiń 10s">
          <span className="skip-text">+10</span>
        </button>

        <button className="audio-btn speed" onClick={changeSpeed}>
          {playbackRate}x
        </button>
      </div>

      <div className="floating-player-progress">
        <div className="progress-track" onClick={handleSeek}>
          <div
            className="progress-fill"
            style={{ width: `${duration ? (localCurrentTime / duration) * 100 : 0}%` }}
          />
        </div>
      </div>

      <div className="floating-player-time">
        {formatTime(localCurrentTime)} / {formatTime(duration)}
      </div>

      {currentUtterance && (
        <div className="floating-player-transcript">
          <span className={`transcript-speaker speaker-${currentUtterance.speaker}`}>
            {currentUtterance.speaker === 'A' ? 'Recepcja' : 'Pacjent'}:
          </span>
          <span className="transcript-text">{currentUtterance.text}</span>
        </div>
      )}

      <div className="floating-player-actions">
        <button className="floating-player-close" onClick={onClose} title="Zamknij">
          {Icons.close}
        </button>
      </div>
    </div>
  );
}

// ============================================
// TRANSCRIPT VIEWER - z podświetlaniem
// ============================================
function TranscriptViewer({ utterances, currentTime, onUtteranceClick }) {
  const activeRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (activeRef.current && containerRef.current) {
      const container = containerRef.current;
      const element = activeRef.current;
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      if (elementRect.top < containerRect.top || elementRect.bottom > containerRect.bottom) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentTime]);

  if (!utterances || utterances.length === 0) {
    return <div className="transcript-empty">Brak transkrypcji</div>;
  }

  const getSpeakerLabel = (speaker) => {
    if (speaker === 'A') return 'Recepcja';
    if (speaker === 'B') return 'Pacjent';
    return speaker;
  };

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="transcript-viewer" ref={containerRef}>
      {utterances.map((utterance, idx) => {
        const isActive = currentTime >= utterance.start && currentTime < utterance.end;

        return (
          <div
            key={idx}
            ref={isActive ? activeRef : null}
            className={`utterance ${isActive ? 'active' : ''} speaker-${utterance.speaker}`}
            onClick={() => onUtteranceClick && onUtteranceClick(utterance.start)}
          >
            <div className="utterance-header">
              <span className={`speaker-badge speaker-${utterance.speaker}`}>
                {getSpeakerLabel(utterance.speaker)}
              </span>
              <span className="utterance-time">{formatTime(utterance.start)}</span>
            </div>
            <p className="utterance-text">{utterance.text}</p>
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// AUDIO PLAYER (embedded version)
// ============================================
function AudioPlayer({ recordingId, utterances, onTimeUpdate }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const time = audioRef.current.currentTime * 1000;
      setCurrentTime(time);
      if (onTimeUpdate) onTimeUpdate(time);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration * 1000);
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    if (audioRef.current) {
      audioRef.current.currentTime = (percentage * duration) / 1000;
    }
  };

  const skip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime += seconds;
    }
  };

  const changeSpeed = () => {
    const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % speeds.length;
    const newRate = speeds[nextIndex];
    setPlaybackRate(newRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
  };

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="audio-player">
      <audio
        ref={audioRef}
        src={`${API_URL}/recordings/${recordingId}/audio`}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      <div className="audio-player-controls">
        <button className="audio-btn skip" onClick={() => skip(-10)} title="Cofnij 10s">
          <span className="skip-text">-10</span>
        </button>

        <button className="audio-btn play" onClick={togglePlay}>
          {isPlaying ? Icons.pause : Icons.play}
        </button>

        <button className="audio-btn skip" onClick={() => skip(10)} title="Przewiń 10s">
          <span className="skip-text">+10</span>
        </button>

        <button className="audio-btn speed" onClick={changeSpeed}>
          {playbackRate}x
        </button>
      </div>

      <div className="audio-player-progress">
        <span className="time-current">{formatTime(currentTime)}</span>
        <div className="progress-track" onClick={handleSeek}>
          <div
            className="progress-fill"
            style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
          />
          {utterances && utterances.map((u, idx) => (
            <div
              key={idx}
              className={`utterance-marker speaker-${u.speaker}`}
              style={{ left: `${duration ? (u.start / duration) * 100 : 0}%` }}
            />
          ))}
        </div>
        <span className="time-duration">{formatTime(duration)}</span>
      </div>
    </div>
  );
}

// ============================================
// QUALITY REPORT - Kompaktowy widok z barami
// ============================================
function QualityReportCompact({ report }) {
  if (!report || !report.overall_score) {
    return null;
  }

  const getScoreColor = (score) => {
    if (score >= 80) return 'var(--success)';
    if (score >= 60) return 'var(--warning)';
    return 'var(--danger)';
  };

  const parseJson = (data) => {
    if (!data) return [];
    if (typeof data === 'string') {
      try { return JSON.parse(data); } catch { return []; }
    }
    return data;
  };

  const detailedScores = parseJson(report.detailed_scores);
  const strengths = parseJson(report.strengths);
  const improvements = parseJson(report.improvements);

  const scoreLabels = {
    greeting: { label: 'Przywitanie', max: 10 },
    empathy: { label: 'Empatia', max: 15 },
    information_gathering: { label: 'Zbieranie informacji', max: 15 },
    solution_proposal: { label: 'Propozycja rozwiązania', max: 15 },
    clarity: { label: 'Jasność komunikacji', max: 10 },
    confirmation: { label: 'Potwierdzenie ustaleń', max: 10 },
    closing: { label: 'Zakończenie', max: 10 },
    no_critical_errors: { label: 'Brak błędów krytycznych', max: 15 }
  };

  return (
    <div className="quality-compact">
      <div className="quality-score-main">
        <div className="score-big" style={{ color: getScoreColor(report.overall_score) }}>
          {report.overall_score}
        </div>
        <div className="score-label">punktów / 100</div>
      </div>

      {detailedScores && Object.keys(detailedScores).length > 0 && (
        <div className="quality-bars">
          {Object.entries(detailedScores).map(([key, value]) => {
            const config = scoreLabels[key] || { label: key, max: 10 };
            const percentage = (value / config.max) * 100;
            return (
              <div key={key} className="score-bar-item">
                <div className="score-bar-header">
                  <span className="score-bar-label">{config.label}</span>
                  <span className="score-bar-value">{value}/{config.max}</span>
                </div>
                <div className="score-bar-track">
                  <div
                    className="score-bar-fill"
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: getScoreColor(percentage)
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="quality-feedback">
        {strengths.length > 0 && (
          <div className="feedback-section good">
            <h5>Co zrobiono dobrze</h5>
            <ul>
              {strengths.slice(0, 3).map((item, idx) => <li key={idx}>{item}</li>)}
            </ul>
          </div>
        )}

        {improvements.length > 0 && (
          <div className="feedback-section improve">
            <h5>Do poprawy</h5>
            <ul>
              {improvements.slice(0, 3).map((item, idx) => <li key={idx}>{item}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// RECORDING DETAIL VIEW
// ============================================
function RecordingDetailView({ recording, onBack, onReviewUpdate, floatingPlayerRecording, floatingPlayerTime, onSeekFloatingPlayer, onStartPlayback }) {
  const [currentAudioTime, setCurrentAudioTime] = useState(0);

  // Use floating player time if this recording is playing in floating player
  const isPlayingInFloatingPlayer = floatingPlayerRecording?.recording_id === recording.recording_id;
  const effectiveCurrentTime = isPlayingInFloatingPlayer ? floatingPlayerTime : currentAudioTime;

  // Handle transcript click - start playback from that point
  const handleTranscriptClick = (timeMs) => {
    if (isPlayingInFloatingPlayer && onSeekFloatingPlayer) {
      // Already playing - just seek
      onSeekFloatingPlayer(timeMs);
    } else if (onStartPlayback) {
      // Not playing - start playback from this time
      onStartPlayback(recording, timeMs);
    }
  };

  const parseUtterances = (data) => {
    if (!data) return [];
    if (typeof data === 'string') {
      try { return JSON.parse(data); } catch { return []; }
    }
    return data;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pl-PL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  const formatDuration = (seconds, ms = null, textLength = null) => {
    // Estimate duration from text length if no valid duration
    // Average: ~150 words/min, ~5 chars/word = ~750 chars/min = ~12.5 chars/sec
    let totalSeconds = 0;

    if (ms && ms > 1000) {
      totalSeconds = Math.floor(ms / 1000);
    } else if (seconds && seconds > 0) {
      totalSeconds = Math.floor(seconds);
    } else if (textLength && textLength > 0) {
      totalSeconds = Math.round(textLength / 12.5);
    }

    if (!totalSeconds) return '--:--';
    const mins = Math.floor(totalSeconds / 60);
    const secs = Math.floor(totalSeconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const utterances = parseUtterances(recording.utterances);

  return (
    <div className="recording-detail">
      <div className="detail-header">
        <button className="back-btn" onClick={onBack}>
          {Icons.arrowLeft}
          <span>Powrót do listy</span>
        </button>

        <div className="detail-meta">
          <span className="meta-item">{Icons.calendar} {formatDate(recording.call_date)}</span>
          <span className="meta-item">{Icons.clock} {formatTime(recording.call_time)}</span>
          <span className="meta-item">{Icons.phone} {formatDuration(recording.duration_seconds, recording.audio_duration_ms, recording.transcription?.length)}</span>
          {recording.agent_id && (
            <span className="meta-item agent">{Icons.user} {recording.agent_id}</span>
          )}
        </div>

        {recording.overall_score !== null && (
          <div className={`detail-score ${recording.overall_score >= 80 ? 'high' : recording.overall_score >= 60 ? 'medium' : 'low'}`}>
            {recording.overall_score} pkt
          </div>
        )}
      </div>

      <div className="detail-player">
        <AudioPlayer
          recordingId={recording.recording_id}
          utterances={utterances}
          onTimeUpdate={setCurrentAudioTime}
        />
      </div>

      {/* Review actions - show for pending recordings OR those requiring review */}
      {(recording.review_status === 'pending' || recording.requires_review) && (
        <div className="detail-review">
          <span className="review-label">
            {recording.requires_review ? 'Ta rozmowa wymaga przeglądu:' : 'Oznacz status rozmowy:'}
          </span>
          <div className="review-buttons">
            <button className="review-btn approve" onClick={() => onReviewUpdate('approved')}>
              {Icons.check} Zatwierdź
            </button>
            <button className="review-btn training" onClick={() => onReviewUpdate('needs_training')}>
              {Icons.user} Szkolenie
            </button>
            <button className="review-btn reject" onClick={() => onReviewUpdate('rejected')}>
              {Icons.close} Odrzuć
            </button>
          </div>
        </div>
      )}

      {/* Show current status badge for already reviewed recordings */}
      {recording.review_status && recording.review_status !== 'pending' && (
        <div className="detail-review-status">
          <span className="review-status-label">Status:</span>
          <span className={`review-status-badge ${recording.review_status}`}>
            {recording.review_status === 'approved' && <>{Icons.check} Zatwierdzone</>}
            {recording.review_status === 'rejected' && <>{Icons.close} Odrzucone</>}
            {recording.review_status === 'needs_training' && <>{Icons.user} Do szkolenia</>}
          </span>
          <button
            className="review-change-btn"
            onClick={() => onReviewUpdate('pending')}
          >
            Zmień status
          </button>
        </div>
      )}

      <div className="detail-content">
        <div className="detail-column transcript-col">
          <div className="column-header">
            <h3>Transkrypcja rozmowy</h3>
            {recording.transcription_confidence && (
              <span className="confidence">
                Pewność: {(recording.transcription_confidence * 100).toFixed(0)}%
              </span>
            )}
          </div>
          <TranscriptViewer
            utterances={utterances}
            currentTime={effectiveCurrentTime}
            onUtteranceClick={handleTranscriptClick}
          />
        </div>

        <div className="detail-column quality-col">
          <div className="column-header">
            <h3>Ocena jakości</h3>
          </div>
          <QualityReportCompact report={recording} />

          {recording.summary && (
            <div className="detail-summary">
              <h4>Podsumowanie</h4>
              <p>{recording.summary}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// RECORDING ROW
// ============================================
function RecordingRow({ recording, onClick, onPlayClick }) {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pl-PL');
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  const formatDuration = (seconds, ms = null, textLength = null) => {
    // Estimate duration from text length if no valid duration
    // Average: ~150 words/min, ~5 chars/word = ~750 chars/min = ~12.5 chars/sec
    let totalSeconds = 0;

    if (ms && ms > 1000) {
      totalSeconds = Math.floor(ms / 1000);
    } else if (seconds && seconds > 0) {
      totalSeconds = Math.floor(seconds);
    } else if (textLength && textLength > 0) {
      totalSeconds = Math.round(textLength / 12.5);
    }

    if (!totalSeconds) return '--:--';
    const mins = Math.floor(totalSeconds / 60);
    const secs = Math.floor(totalSeconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreClass = (score) => {
    if (score >= 80) return 'high';
    if (score >= 60) return 'medium';
    return 'low';
  };

  // Determine status class for colored border
  const getStatusClass = () => {
    if (recording.review_status === 'approved') return 'status-approved';
    if (recording.review_status === 'rejected') return 'status-rejected';
    if (recording.review_status === 'needs_training') return 'status-training';
    if (recording.requires_review) return 'status-needs-review';
    if (recording.review_status === 'pending') return 'status-pending';
    return '';
  };

  return (
    <div
      className={`recording-row ${getStatusClass()}`}
      onClick={onClick}
    >
      <div className="recording-icon">
        {recording.direction === 'in' ? Icons.phoneIn : Icons.phoneOut}
      </div>

      <div className="recording-info">
        <div className="recording-title">
          {recording.agent_id && <span className="agent-tag">{recording.agent_id}</span>}
          <span className="recording-datetime">
            {formatDate(recording.call_date)} o {formatTime(recording.call_time)}
          </span>
          <span className="recording-duration">{Icons.clock} {formatDuration(recording.duration_seconds, recording.audio_duration_ms, recording.transcription?.length)}</span>
        </div>
        {recording.summary && (
          <div className="recording-summary">{recording.summary}</div>
        )}
      </div>

      <div className="recording-score">
        {recording.overall_score !== null ? (
          <span className={`score-badge ${getScoreClass(recording.overall_score)}`}>
            {recording.overall_score}
          </span>
        ) : (
          <span className="score-badge pending">—</span>
        )}
      </div>

      {recording.requires_review && (
        <div className="review-flag" title="Wymaga przeglądu">
          {Icons.alert}
        </div>
      )}

      <button
        className="recording-play-btn"
        onClick={(e) => {
          e.stopPropagation();
          if (onPlayClick) onPlayClick(recording);
        }}
        title="Odtwórz"
      >
        {Icons.play}
      </button>

      <div className="recording-action">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </div>
    </div>
  );
}

// ============================================
// AGENT CARD
// ============================================
function AgentCard({ agent, onClick }) {
  const getScoreClass = (score) => {
    if (score >= 80) return 'high';
    if (score >= 60) return 'medium';
    return 'low';
  };

  return (
    <div className="agent-card" onClick={onClick}>
      <div className="agent-avatar">
        {agent.agent_id.charAt(0).toUpperCase()}
      </div>
      <div className="agent-info">
        <h4>{agent.agent_id}</h4>
        <span className="agent-stats">
          {agent.total_calls} rozmów
          {agent.flagged_calls > 0 && <span className="flagged"> • {agent.flagged_calls} do przeglądu</span>}
        </span>
      </div>
      <div className={`agent-score ${getScoreClass(agent.avg_score)}`}>
        {Math.round(agent.avg_score)}
      </div>
    </div>
  );
}

// ============================================
// REPORTS SECTION
// ============================================
function ReportsSection({ recordings, agentStats, dailyStats }) {
  const [reportType, setReportType] = useState('weekly');
  const [selectedAgent, setSelectedAgent] = useState('all');

  // Calculate report data
  const calculateWeeklyData = () => {
    const weeks = [];
    const now = new Date();

    for (let i = 0; i < 4; i++) {
      const weekEnd = new Date(now);
      weekEnd.setDate(weekEnd.getDate() - (i * 7));
      const weekStart = new Date(weekEnd);
      weekStart.setDate(weekStart.getDate() - 6);

      const weekRecordings = recordings.filter(r => {
        const date = new Date(r.call_date);
        return date >= weekStart && date <= weekEnd;
      });

      const avgScore = weekRecordings.length > 0
        ? Math.round(weekRecordings.reduce((sum, r) => sum + (r.overall_score || 0), 0) / weekRecordings.length)
        : 0;

      weeks.push({
        label: `${weekStart.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' })} - ${weekEnd.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' })}`,
        total: weekRecordings.length,
        avgScore,
        highQuality: weekRecordings.filter(r => r.overall_score >= 80).length,
        lowQuality: weekRecordings.filter(r => r.overall_score < 60).length,
        needsReview: weekRecordings.filter(r => r.requires_review).length
      });
    }

    return weeks.reverse();
  };

  const weeklyData = calculateWeeklyData();

  // Calculate agent comparison
  const agentComparison = agentStats.map(agent => {
    const trend = Math.random() > 0.5 ? 'up' : 'down'; // In real app, calculate from historical data
    const change = Math.floor(Math.random() * 10) - 5;
    return { ...agent, trend, change };
  });

  // Calculate totals
  const totals = {
    totalCalls: recordings.length,
    avgScore: recordings.length > 0
      ? Math.round(recordings.reduce((sum, r) => sum + (r.overall_score || 0), 0) / recordings.length)
      : 0,
    highQuality: recordings.filter(r => r.overall_score >= 80).length,
    lowQuality: recordings.filter(r => r.overall_score < 60).length,
    needsReview: recordings.filter(r => r.requires_review && r.review_status === 'pending').length
  };

  return (
    <div className="section-reports">
      {/* Report Type Selector */}
      <div className="report-controls">
        <div className="report-tabs">
          <button
            className={`report-tab ${reportType === 'weekly' ? 'active' : ''}`}
            onClick={() => setReportType('weekly')}
          >
            Tygodniowy
          </button>
          <button
            className={`report-tab ${reportType === 'agents' ? 'active' : ''}`}
            onClick={() => setReportType('agents')}
          >
            Agenci
          </button>
          <button
            className={`report-tab ${reportType === 'trends' ? 'active' : ''}`}
            onClick={() => setReportType('trends')}
          >
            Trendy
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="report-summary-cards">
        <div className="report-card">
          <div className="report-card-value">{totals.totalCalls}</div>
          <div className="report-card-label">Wszystkie rozmowy</div>
        </div>
        <div className="report-card accent">
          <div className="report-card-value">{totals.avgScore}</div>
          <div className="report-card-label">Średnia ocena</div>
        </div>
        <div className="report-card success">
          <div className="report-card-value">{totals.highQuality}</div>
          <div className="report-card-label">Wysoka jakość (80+)</div>
        </div>
        <div className="report-card danger">
          <div className="report-card-value">{totals.lowQuality}</div>
          <div className="report-card-label">Niska jakość (&lt;60)</div>
        </div>
      </div>

      {/* Weekly Report */}
      {reportType === 'weekly' && (
        <div className="report-section">
          <h3>Raport tygodniowy</h3>
          <div className="weekly-chart">
            {weeklyData.map((week, idx) => (
              <div key={idx} className="week-column">
                <div className="week-bar-container">
                  <div
                    className="week-bar"
                    style={{ height: `${Math.min(week.total * 10, 100)}%` }}
                  >
                    <span className="week-bar-value">{week.total}</span>
                  </div>
                </div>
                <div className="week-label">{week.label}</div>
                <div className="week-score">
                  <span className={`score-pill ${week.avgScore >= 80 ? 'high' : week.avgScore >= 60 ? 'medium' : 'low'}`}>
                    {week.avgScore}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="weekly-table">
            <table>
              <thead>
                <tr>
                  <th>Tydzień</th>
                  <th>Rozmowy</th>
                  <th>Średnia</th>
                  <th>Wysoka jakość</th>
                  <th>Niska jakość</th>
                  <th>Do przeglądu</th>
                </tr>
              </thead>
              <tbody>
                {weeklyData.map((week, idx) => (
                  <tr key={idx}>
                    <td>{week.label}</td>
                    <td>{week.total}</td>
                    <td>
                      <span className={`score-pill ${week.avgScore >= 80 ? 'high' : week.avgScore >= 60 ? 'medium' : 'low'}`}>
                        {week.avgScore}
                      </span>
                    </td>
                    <td className="text-success">{week.highQuality}</td>
                    <td className="text-danger">{week.lowQuality}</td>
                    <td>{week.needsReview}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Agents Report */}
      {reportType === 'agents' && (
        <div className="report-section">
          <h3>Porównanie agentów</h3>
          <div className="agents-comparison">
            {agentComparison.map((agent, idx) => (
              <div key={idx} className="agent-report-card">
                <div className="agent-report-header">
                  <div className="agent-report-avatar">
                    {agent.agent_id.charAt(0).toUpperCase()}
                  </div>
                  <div className="agent-report-info">
                    <h4>{agent.agent_id}</h4>
                    <span>{agent.total_calls} rozmów</span>
                  </div>
                  <div className={`agent-report-score ${agent.avg_score >= 80 ? 'high' : agent.avg_score >= 60 ? 'medium' : 'low'}`}>
                    {Math.round(agent.avg_score)}
                  </div>
                </div>
                <div className="agent-report-stats">
                  <div className="agent-stat">
                    <span className="agent-stat-label">Wysoka jakość</span>
                    <span className="agent-stat-value text-success">
                      {Math.round((agent.total_calls - (agent.flagged_calls || 0)) / agent.total_calls * 100)}%
                    </span>
                  </div>
                  <div className="agent-stat">
                    <span className="agent-stat-label">Do przeglądu</span>
                    <span className="agent-stat-value text-warning">{agent.flagged_calls || 0}</span>
                  </div>
                  <div className="agent-stat">
                    <span className="agent-stat-label">Trend</span>
                    <span className={`agent-stat-value ${agent.trend === 'up' ? 'text-success' : 'text-danger'}`}>
                      {agent.trend === 'up' ? Icons.trendUp : Icons.trendDown}
                      {agent.change > 0 ? '+' : ''}{agent.change}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trends Report */}
      {reportType === 'trends' && (
        <div className="report-section">
          <h3>Trendy jakości</h3>
          <div className="trends-grid">
            <div className="trend-card">
              <h4>Średnia ocena w czasie</h4>
              <div className="trend-chart">
                {dailyStats.slice(0, 14).reverse().map((day, idx) => (
                  <div
                    key={idx}
                    className="trend-bar"
                    style={{
                      height: `${day.avg_score || 0}%`,
                      backgroundColor: (day.avg_score || 0) >= 80 ? 'var(--success)' : (day.avg_score || 0) >= 60 ? 'var(--warning)' : 'var(--danger)'
                    }}
                    title={`${new Date(day.call_date).toLocaleDateString('pl-PL')}: ${Math.round(day.avg_score || 0)}`}
                  />
                ))}
              </div>
              <div className="trend-labels">
                <span>14 dni temu</span>
                <span>Dziś</span>
              </div>
            </div>

            <div className="trend-card">
              <h4>Liczba rozmów dziennie</h4>
              <div className="trend-chart">
                {dailyStats.slice(0, 14).reverse().map((day, idx) => (
                  <div
                    key={idx}
                    className="trend-bar calls"
                    style={{
                      height: `${Math.min((day.total_calls || day.total || 0) * 20, 100)}%`
                    }}
                    title={`${new Date(day.call_date).toLocaleDateString('pl-PL')}: ${day.total_calls || day.total || 0} rozmów`}
                  />
                ))}
              </div>
              <div className="trend-labels">
                <span>14 dni temu</span>
                <span>Dziś</span>
              </div>
            </div>
          </div>

          <div className="insights-section">
            <h4>Wnioski</h4>
            <ul className="insights-list">
              <li className={totals.avgScore >= 75 ? 'positive' : 'negative'}>
                {totals.avgScore >= 75
                  ? `Średnia ocena ${totals.avgScore} punktów jest powyżej celu (75)`
                  : `Średnia ocena ${totals.avgScore} punktów jest poniżej celu (75)`
                }
              </li>
              <li className={totals.needsReview === 0 ? 'positive' : 'neutral'}>
                {totals.needsReview === 0
                  ? 'Wszystkie rozmowy zostały przejrzane'
                  : `${totals.needsReview} rozmów wymaga przeglądu`
                }
              </li>
              <li className="neutral">
                {`${Math.round(totals.highQuality / totals.totalCalls * 100)}% rozmów ma ocenę 80+`}
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// TRAINING SECTION - Szkolenia z rozmów
// ============================================
function TrainingSection({ recordings }) {
  const [selectedCase, setSelectedCase] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [completedQuizzes, setCompletedQuizzes] = useState(() => {
    const saved = localStorage.getItem('completed_training_quizzes');
    return saved ? JSON.parse(saved) : [];
  });

  // Filter recordings that need training
  const trainingRecordings = recordings.filter(r => r.review_status === 'needs_training');

  // Generate quiz questions from improvements and risks
  const generateQuizFromRecording = (recording) => {
    const questions = [];

    // Parse improvements and risks from JSON strings if needed
    let improvements = [];
    let risks = [];
    let recommendations = [];

    try {
      improvements = typeof recording.improvements === 'string'
        ? JSON.parse(recording.improvements)
        : (recording.improvements || []);
    } catch { improvements = []; }

    try {
      risks = typeof recording.risks === 'string'
        ? JSON.parse(recording.risks)
        : (recording.risks || []);
    } catch { risks = []; }

    try {
      recommendations = typeof recording.recommendations === 'string'
        ? JSON.parse(recording.recommendations)
        : (recording.recommendations || []);
    } catch { recommendations = []; }

    // Generate questions based on improvements
    if (improvements.length > 0) {
      improvements.forEach((improvement, idx) => {
        questions.push({
          id: `imp_${idx}`,
          type: 'improvement',
          question: `Co należało zrobić lepiej w tej sytuacji?`,
          context: improvement,
          options: [
            { id: 'a', text: improvement, correct: true },
            { id: 'b', text: 'Zakończyć rozmowę jak najszybciej', correct: false },
            { id: 'c', text: 'Przekierować do kierownika bez wyjaśnienia', correct: false },
            { id: 'd', text: 'Zignorować problem pacjenta', correct: false }
          ]
        });
      });
    }

    // Generate questions based on risks
    if (risks.length > 0) {
      questions.push({
        id: 'risk_awareness',
        type: 'risk',
        question: 'Jakie ryzyka zostały zidentyfikowane w tej rozmowie?',
        context: 'Zaznacz prawidłowe odpowiedzi',
        multiSelect: true,
        options: [
          ...risks.slice(0, 3).map((risk, idx) => ({ id: `r${idx}`, text: risk, correct: true })),
          { id: 'r_wrong', text: 'Brak żadnych ryzyk - rozmowa przebiegła idealnie', correct: false }
        ]
      });
    }

    // Add best practice question
    if (recommendations.length > 0) {
      questions.push({
        id: 'best_practice',
        type: 'recommendation',
        question: 'Która z rekomendacji jest najważniejsza do wdrożenia?',
        context: 'Wybierz najlepszą praktykę',
        options: [
          { id: 'rec_a', text: recommendations[0], correct: true },
          { id: 'rec_b', text: 'Nie podejmować żadnych działań', correct: false },
          { id: 'rec_c', text: 'Obwiniać pacjenta za trudną rozmowę', correct: false }
        ]
      });
    }

    return questions;
  };

  const handleAnswerSelect = (questionId, answerId, isMultiSelect = false) => {
    setQuizAnswers(prev => {
      if (isMultiSelect) {
        const current = prev[questionId] || [];
        if (current.includes(answerId)) {
          return { ...prev, [questionId]: current.filter(id => id !== answerId) };
        } else {
          return { ...prev, [questionId]: [...current, answerId] };
        }
      }
      return { ...prev, [questionId]: answerId };
    });
  };

  const calculateScore = (questions) => {
    let correct = 0;
    let total = questions.length;

    questions.forEach(q => {
      const userAnswer = quizAnswers[q.id];
      if (q.multiSelect) {
        const correctAnswers = q.options.filter(o => o.correct).map(o => o.id).sort();
        const userAnswers = (userAnswer || []).sort();
        if (JSON.stringify(correctAnswers) === JSON.stringify(userAnswers)) {
          correct++;
        }
      } else {
        const correctOption = q.options.find(o => o.correct);
        if (userAnswer === correctOption?.id) {
          correct++;
        }
      }
    });

    return { correct, total, percentage: Math.round((correct / total) * 100) };
  };

  const handleSubmitQuiz = (recordingId, questions) => {
    setShowResults(true);
    const score = calculateScore(questions);

    if (score.percentage >= 70) {
      const newCompleted = [...completedQuizzes, {
        recordingId,
        score: score.percentage,
        date: new Date().toISOString()
      }];
      setCompletedQuizzes(newCompleted);
      localStorage.setItem('completed_training_quizzes', JSON.stringify(newCompleted));
    }
  };

  const resetQuiz = () => {
    setQuizAnswers({});
    setShowResults(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pl-PL');
  };

  const isQuizCompleted = (recordingId) => {
    return completedQuizzes.some(q => q.recordingId === recordingId);
  };

  // Stats
  const stats = {
    total: trainingRecordings.length,
    completed: completedQuizzes.length,
    pending: trainingRecordings.length - completedQuizzes.filter(c =>
      trainingRecordings.some(r => r.recording_id === c.recordingId)
    ).length
  };

  return (
    <div className="section-training">
      {/* Stats Cards */}
      <div className="stats-cards">
        <div className="stat-card warning">
          <div className="stat-icon">{Icons.training}</div>
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Rozmów do przeszkolenia</span>
          </div>
        </div>
        <div className="stat-card success">
          <div className="stat-icon">{Icons.check}</div>
          <div className="stat-content">
            <span className="stat-value">{stats.completed}</span>
            <span className="stat-label">Ukończonych quizów</span>
          </div>
        </div>
        <div className="stat-card accent">
          <div className="stat-icon">{Icons.quiz}</div>
          <div className="stat-content">
            <span className="stat-value">{stats.pending}</span>
            <span className="stat-label">Oczekujących</span>
          </div>
        </div>
      </div>

      {selectedCase ? (
        // Quiz View - 2 Column Layout
        <div className="training-quiz-view">
          <div className="quiz-top-bar">
            <button className="back-btn" onClick={() => { setSelectedCase(null); resetQuiz(); }}>
              {Icons.arrowLeft}
              <span>Powrót do listy</span>
            </button>
            <div className="quiz-header-info">
              <span className="quiz-agent">{Icons.user} {selectedCase.agent_id}</span>
              <span className="quiz-date">{Icons.calendar} {formatDate(selectedCase.call_date)}</span>
              <span className={`quiz-score-badge ${selectedCase.overall_score >= 60 ? 'medium' : 'low'}`}>
                Ocena: {selectedCase.overall_score}/100
              </span>
            </div>
          </div>

          {/* Context Banner */}
          <div className="quiz-context-banner">
            <div className="context-icon">{Icons.lightbulb}</div>
            <div className="context-text">
              <strong>Kontekst:</strong> {selectedCase.summary}
            </div>
          </div>

          {/* 2 Column Layout */}
          <div className="training-two-columns">
            {/* Left Column - Player & Transcript */}
            <div className="training-left-column">
              {/* Audio Player */}
              <div className="quiz-audio-player">
                <div className="player-header">
                  <span className="player-icon">{Icons.recordings}</span>
                  <span>Nagranie rozmowy</span>
                </div>
                <audio
                  controls
                  src={`/api/recordings/${selectedCase.recording_id}/audio`}
                  className="audio-element"
                >
                  Twoja przeglądarka nie obsługuje odtwarzania audio.
                </audio>
              </div>

              {/* Transcript with speakers */}
              <div className="quiz-transcript-speakers">
                <h3>{Icons.message} Transkrypcja rozmowy</h3>
                <div className="transcript-speakers-container">
                  {(() => {
                    let utterances = [];
                    try {
                      utterances = typeof selectedCase.utterances === 'string'
                        ? JSON.parse(selectedCase.utterances)
                        : (selectedCase.utterances || []);
                    } catch { utterances = []; }

                    if (utterances.length > 0) {
                      return (
                        <div className="transcript-conversation">
                          {utterances.map((utt, idx) => (
                            <div
                              key={idx}
                              className={`transcript-message ${utt.speaker === 'A' ? 'speaker-a' : 'speaker-b'}`}
                            >
                              <div className="message-header">
                                <span className="speaker-icon">
                                  {utt.speaker === 'A' ? Icons.speakerA : Icons.speakerB}
                                </span>
                                <span className="speaker-name">
                                  {utt.speaker === 'A' ? 'Recepcja' : 'Pacjent'}
                                </span>
                              </div>
                              <div className="message-text">{utt.text}</div>
                            </div>
                          ))}
                        </div>
                      );
                    } else {
                      return (
                        <div className="transcript-plain">
                          {selectedCase.transcription}
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>
            </div>

            {/* Right Column - Quiz */}
            <div className="training-right-column">
              <div className="quiz-questions">
                <div className="quiz-questions-header">
                  <h3>{Icons.quiz} Quiz szkoleniowy</h3>
                  <p className="quiz-instructions">
                    Odpowiedz na pytania dotyczące analizowanej rozmowy.
                    <br/>Wymagane minimum <strong>70%</strong> poprawnych odpowiedzi.
                  </p>
                </div>

                {generateQuizFromRecording(selectedCase).map((question, qIdx) => {
                  const userAnswer = quizAnswers[question.id];

                  return (
                    <div key={question.id} className={`quiz-question ${showResults ? 'show-results' : ''}`}>
                      <div className="question-header-row">
                        <span className="question-number">Pytanie {qIdx + 1}</span>
                        {question.multiSelect && (
                          <span className="question-type-badge">Wielokrotny wybór</span>
                        )}
                      </div>
                      <div className="question-text">{question.question}</div>
                      {question.context && (
                        <div className="question-context">
                          <span className="context-label">{Icons.lightbulb} Wskazówka:</span> {question.context}
                        </div>
                      )}

                      <div className="question-options">
                        {question.options.map(option => {
                          const isSelected = question.multiSelect
                            ? (userAnswer || []).includes(option.id)
                            : userAnswer === option.id;
                          const showCorrect = showResults && option.correct;
                          const showWrong = showResults && isSelected && !option.correct;

                          return (
                            <button
                              key={option.id}
                              className={`quiz-option ${isSelected ? 'selected' : ''} ${showCorrect ? 'correct' : ''} ${showWrong ? 'wrong' : ''}`}
                              onClick={() => !showResults && handleAnswerSelect(question.id, option.id, question.multiSelect)}
                              disabled={showResults}
                            >
                              <span className="option-marker">
                                {showResults ? (option.correct ? Icons.correct : (isSelected ? Icons.wrong : null)) : null}
                              </span>
                              <span className="option-text">{option.text}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Submit / Results */}
                {!showResults ? (
                  <button
                    className="quiz-submit-btn"
                    onClick={() => handleSubmitQuiz(selectedCase.recording_id, generateQuizFromRecording(selectedCase))}
                    disabled={Object.keys(quizAnswers).length < generateQuizFromRecording(selectedCase).length}
                  >
                    Sprawdź odpowiedzi
                  </button>
                ) : (
                  <div className="quiz-results">
                    {(() => {
                      const score = calculateScore(generateQuizFromRecording(selectedCase));
                      return (
                        <>
                          <div className={`results-score ${score.percentage >= 70 ? 'pass' : 'fail'}`}>
                            <span className="score-icon">
                              {score.percentage >= 70 ? Icons.correct : Icons.alert}
                            </span>
                            <span className="score-text">
                              Wynik: {score.correct}/{score.total} ({score.percentage}%)
                            </span>
                          </div>
                          <p className="results-message">
                            {score.percentage >= 70
                              ? 'Gratulacje! Szkolenie zostało zaliczone. Wynik zapisany.'
                              : 'Spróbuj ponownie. Wymagane minimum to 70%.'}
                          </p>
                          <button className="quiz-retry-btn" onClick={resetQuiz}>
                            Spróbuj ponownie
                          </button>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        // List View
        <>
          {trainingRecordings.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">{Icons.training}</div>
              <h4>Brak rozmów do przeszkolenia</h4>
              <p>Wszystkie rozmowy zostały przejrzane. Nowe szkolenia pojawią się automatycznie.</p>
            </div>
          ) : (
            <div className="training-list-container">
              <div className="training-list-header">
                <h3>Rozmowy wymagające szkolenia ({trainingRecordings.length})</h3>
              </div>

              <div className="training-list">
                {trainingRecordings.map(recording => {
                  const completed = isQuizCompleted(recording.recording_id);
                  let improvements = [];
                  try {
                    improvements = typeof recording.improvements === 'string'
                      ? JSON.parse(recording.improvements)
                      : (recording.improvements || []);
                  } catch { improvements = []; }

                  return (
                    <div
                      key={recording.recording_id}
                      className={`training-row ${completed ? 'completed' : ''}`}
                      onClick={() => setSelectedCase(recording)}
                    >
                      <div className="training-icon">
                        {completed ? Icons.correct : Icons.training}
                      </div>

                      <div className="training-info">
                        <div className="training-header">
                          <span className="training-agent">{recording.agent_id}</span>
                          <span className="training-date">{formatDate(recording.call_date)}</span>
                          <span className={`training-score ${recording.overall_score >= 60 ? 'medium' : 'low'}`}>
                            {recording.overall_score}/100
                          </span>
                          {completed && <span className="completed-badge">{Icons.check} Ukończone</span>}
                        </div>

                        <div className="training-summary">{recording.summary}</div>

                        {improvements.length > 0 && (
                          <div className="training-improvements">
                            <span className="improvements-label">{Icons.lightbulb} Do poprawy:</span>
                            <ul>
                              {improvements.slice(0, 2).map((imp, idx) => (
                                <li key={idx}>{imp}</li>
                              ))}
                              {improvements.length > 2 && (
                                <li className="more">+{improvements.length - 2} więcej...</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>

                      <button className="training-start-btn">
                        {completed ? 'Powtórz' : 'Rozpocznij'} {Icons.arrowRight}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Info Banner */}
          <div className="training-info-banner">
            <div className="banner-icon">{Icons.graduation}</div>
            <div className="banner-content">
              <h4>System szkoleń z analizy rozmów</h4>
              <p>
                Quizy szkoleniowe są automatycznie generowane na podstawie analiz AI.
                Każde szkolenie zawiera kontekst rozmowy, fragment transkrypcji oraz pytania
                sprawdzające wiedzę z zakresu obsługi pacjenta. Zaliczenie wymaga minimum 70% poprawnych odpowiedzi.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================
// SALES SECTION - Okazje sprzedażowe
// ============================================
function SalesSection() {
  // Mock data for sales opportunities detected from calls
  const salesOpportunities = [
    {
      id: 1,
      service: 'Implant',
      patient_phone: '+48 500 XXX 123',
      detected_date: '2025-01-17',
      call_date: '2025-01-17',
      call_time: '10:30',
      agent_id: 'Recepcja',
      context: 'Pacjent pytał o możliwość wszczepienia implantu w miejsce usuniętego zęba. Wyrażał zainteresowanie ceną i czasem zabiegu.',
      status: 'new',
      priority: 'high',
      estimated_value: 4500
    },
    {
      id: 2,
      service: 'Wybielanie',
      patient_phone: '+48 600 XXX 456',
      detected_date: '2025-01-16',
      call_date: '2025-01-16',
      call_time: '14:15',
      agent_id: 'Recepcja',
      context: 'Pacjentka zainteresowana zabiegiem wybielania zębów przed ślubem. Pytała o dostępne terminy i metody.',
      status: 'contacted',
      priority: 'medium',
      estimated_value: 1200
    },
    {
      id: 3,
      service: 'Aparat ortodontyczny',
      patient_phone: '+48 700 XXX 789',
      detected_date: '2025-01-15',
      call_date: '2025-01-15',
      call_time: '11:00',
      agent_id: 'Recepcja',
      context: 'Mama dzwoniła w sprawie aparatu dla nastoletniej córki. Zainteresowana aparatami przezroczystymi.',
      status: 'scheduled',
      priority: 'high',
      estimated_value: 8000
    },
    {
      id: 4,
      service: 'Licówki',
      patient_phone: '+48 510 XXX 321',
      detected_date: '2025-01-14',
      call_date: '2025-01-14',
      call_time: '09:45',
      agent_id: 'Recepcja',
      context: 'Pacjent pytał o możliwość poprawy estetyki przednich zębów. Zainteresowany licówkami porcelanowymi.',
      status: 'new',
      priority: 'medium',
      estimated_value: 6000
    },
    {
      id: 5,
      service: 'Higienizacja',
      patient_phone: '+48 660 XXX 654',
      detected_date: '2025-01-13',
      call_date: '2025-01-13',
      call_time: '16:30',
      agent_id: 'Recepcja',
      context: 'Pacjent dzwonił w sprawie bólu. Zaproponowano również profesjonalną higienizację - wyraził zainteresowanie.',
      status: 'converted',
      priority: 'low',
      estimated_value: 350
    },
    {
      id: 6,
      service: 'Most protetyczny',
      patient_phone: '+48 530 XXX 987',
      detected_date: '2025-01-12',
      call_date: '2025-01-12',
      call_time: '12:00',
      agent_id: 'Recepcja',
      context: 'Pacjent szuka rozwiązania na brakujące zęby. Zainteresowany mostem jako alternatywą dla implantów.',
      status: 'contacted',
      priority: 'high',
      estimated_value: 3500
    }
  ];

  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');

  const getServiceIcon = (service) => {
    switch (service.toLowerCase()) {
      case 'implant':
      case 'most protetyczny':
        return Icons.tooth;
      case 'wybielanie':
      case 'licówki':
        return Icons.sparkles;
      case 'aparat ortodontyczny':
        return Icons.graduation;
      default:
        return Icons.target;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return { label: 'Nowy', className: 'new' };
      case 'contacted':
        return { label: 'Skontaktowano', className: 'contacted' };
      case 'scheduled':
        return { label: 'Umówiono', className: 'scheduled' };
      case 'converted':
        return { label: 'Skonwertowano', className: 'converted' };
      case 'lost':
        return { label: 'Utracono', className: 'lost' };
      default:
        return { label: status, className: '' };
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high':
        return { label: 'Wysoki', className: 'high' };
      case 'medium':
        return { label: 'Średni', className: 'medium' };
      case 'low':
        return { label: 'Niski', className: 'low' };
      default:
        return { label: priority, className: '' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pl-PL');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Filter opportunities
  const filteredOpportunities = salesOpportunities.filter(opp => {
    if (statusFilter !== 'all' && opp.status !== statusFilter) return false;
    if (serviceFilter !== 'all' && opp.service !== serviceFilter) return false;
    return true;
  });

  // Calculate stats
  const stats = {
    total: salesOpportunities.length,
    new: salesOpportunities.filter(o => o.status === 'new').length,
    inProgress: salesOpportunities.filter(o => ['contacted', 'scheduled'].includes(o.status)).length,
    converted: salesOpportunities.filter(o => o.status === 'converted').length,
    totalValue: salesOpportunities.reduce((sum, o) => sum + o.estimated_value, 0),
    convertedValue: salesOpportunities.filter(o => o.status === 'converted').reduce((sum, o) => sum + o.estimated_value, 0)
  };

  // Get unique services for filter
  const services = [...new Set(salesOpportunities.map(o => o.service))];

  return (
    <div className="section-sales">
      {/* Stats Cards */}
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-icon">{Icons.target}</div>
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Wszystkie okazje</span>
          </div>
        </div>
        <div className="stat-card warning">
          <div className="stat-icon">{Icons.alert}</div>
          <div className="stat-content">
            <span className="stat-value">{stats.new}</span>
            <span className="stat-label">Nowe do obsługi</span>
          </div>
        </div>
        <div className="stat-card accent">
          <div className="stat-icon">{Icons.phone}</div>
          <div className="stat-content">
            <span className="stat-value">{stats.inProgress}</span>
            <span className="stat-label">W trakcie</span>
          </div>
        </div>
        <div className="stat-card success">
          <div className="stat-icon">{Icons.check}</div>
          <div className="stat-content">
            <span className="stat-value">{stats.converted}</span>
            <span className="stat-label">Skonwertowane</span>
          </div>
        </div>
      </div>

      {/* Value Summary */}
      <div className="sales-value-summary">
        <div className="value-card">
          <div className="value-label">Potencjalna wartość</div>
          <div className="value-amount">{formatCurrency(stats.totalValue)}</div>
        </div>
        <div className="value-card converted">
          <div className="value-label">Wartość skonwertowana</div>
          <div className="value-amount">{formatCurrency(stats.convertedValue)}</div>
        </div>
        <div className="value-card rate">
          <div className="value-label">Konwersja</div>
          <div className="value-amount">{stats.total > 0 ? Math.round((stats.converted / stats.total) * 100) : 0}%</div>
        </div>
      </div>

      {/* Filters */}
      <div className="sales-filters">
        <div className="filter-group">
          <label>Status:</label>
          <div className="filter-buttons">
            <button
              className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              Wszystkie
            </button>
            <button
              className={`filter-btn new ${statusFilter === 'new' ? 'active' : ''}`}
              onClick={() => setStatusFilter('new')}
            >
              Nowe
            </button>
            <button
              className={`filter-btn contacted ${statusFilter === 'contacted' ? 'active' : ''}`}
              onClick={() => setStatusFilter('contacted')}
            >
              Skontaktowano
            </button>
            <button
              className={`filter-btn scheduled ${statusFilter === 'scheduled' ? 'active' : ''}`}
              onClick={() => setStatusFilter('scheduled')}
            >
              Umówiono
            </button>
            <button
              className={`filter-btn converted ${statusFilter === 'converted' ? 'active' : ''}`}
              onClick={() => setStatusFilter('converted')}
            >
              Skonwertowano
            </button>
          </div>
        </div>

        <div className="filter-group">
          <label>Usługa:</label>
          <CustomSelect
            value={serviceFilter}
            onChange={(val) => setServiceFilter(val)}
            placeholder="Wszystkie usługi"
            options={[
              { value: 'all', label: 'Wszystkie usługi' },
              ...services.map(s => ({ value: s, label: s }))
            ]}
          />
        </div>
      </div>

      {/* Opportunities List */}
      <div className="sales-list-container">
        <div className="sales-list-header">
          <h3>Okazje sprzedażowe ({filteredOpportunities.length})</h3>
          <span className="ai-badge">{Icons.sparkles} Wykryte przez AI</span>
        </div>

        {filteredOpportunities.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">{Icons.target}</div>
            <h4>Brak okazji</h4>
            <p>Nie znaleziono okazji spełniających kryteria</p>
          </div>
        ) : (
          <div className="sales-list">
            {filteredOpportunities.map((opportunity) => {
              const statusBadge = getStatusBadge(opportunity.status);
              const priorityBadge = getPriorityBadge(opportunity.priority);

              return (
                <div key={opportunity.id} className={`sales-row priority-${opportunity.priority}`}>
                  <div className="sales-icon">
                    {getServiceIcon(opportunity.service)}
                  </div>

                  <div className="sales-info">
                    <div className="sales-header">
                      <span className="sales-service">{opportunity.service}</span>
                      <span className={`sales-status ${statusBadge.className}`}>
                        {statusBadge.label}
                      </span>
                      <span className={`sales-priority ${priorityBadge.className}`}>
                        {priorityBadge.label}
                      </span>
                    </div>
                    <div className="sales-context">{opportunity.context}</div>
                    <div className="sales-meta">
                      <span>{Icons.calendar} {formatDate(opportunity.call_date)}</span>
                      <span>{Icons.clock} {opportunity.call_time}</span>
                      <span>{Icons.user} {opportunity.agent_id}</span>
                      <span>{Icons.phone} {opportunity.patient_phone}</span>
                    </div>
                  </div>

                  <div className="sales-value">
                    <span className="value-amount">{formatCurrency(opportunity.estimated_value)}</span>
                    <span className="value-label">szacowana wartość</span>
                  </div>

                  <div className="sales-actions">
                    <button className="action-btn primary" title="Zadzwoń">
                      {Icons.phone}
                    </button>
                    <button className="action-btn" title="Szczegóły">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="sales-info-banner">
        <div className="banner-icon">{Icons.sparkles}</div>
        <div className="banner-content">
          <h4>Automatyczne wykrywanie okazji sprzedażowych</h4>
          <p>
            Ta sekcja prezentuje okazje sprzedażowe automatycznie wykryte przez AI podczas analizy rozmów telefonicznych.
            System identyfikuje zainteresowanie pacjentów dodatkowymi usługami takimi jak implanty, wybielanie, aparaty ortodontyczne i inne.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================
// MAIN APP
// ============================================
function App() {
  // Authentication state - must be first
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem(SESSION_KEY) === 'authenticated';
  });

  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'light';
  });

  const [activeSection, setActiveSection] = useState('recordings');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [recordings, setRecordings] = useState([]);
  const [recordingsStats, setRecordingsStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [agentStats, setAgentStats] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    requires_review: false,
    agent_id: '',
    min_score: '',
    max_score: '',
    review_status: '' // '', 'approved', 'rejected', 'needs_training'
  });

  // Sorting state
  const [sortBy, setSortBy] = useState('date'); // 'date', 'score_asc', 'score_desc'

  // Date tabs state
  const [dateTab, setDateTab] = useState('all'); // 'all', 'today', 'yesterday', 'week', 'month'

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [recordingsPerPage] = useState(15);

  const [selectedRecording, setSelectedRecording] = useState(null);
  const [recordingDetails, setRecordingDetails] = useState(null);

  // Floating player state
  const [floatingPlayerRecording, setFloatingPlayerRecording] = useState(null);
  const [floatingPlayerUtterances, setFloatingPlayerUtterances] = useState([]);
  const [floatingPlayerSeekTo, setFloatingPlayerSeekTo] = useState(null);
  const [floatingPlayerTime, setFloatingPlayerTime] = useState(0);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.requires_review) params.append('requires_review', 'true');
      if (filters.agent_id) params.append('agent_id', filters.agent_id);
      if (filters.min_score) params.append('min_score', filters.min_score);
      if (filters.max_score) params.append('max_score', filters.max_score);
      if (filters.review_status) params.append('review_status', filters.review_status);

      const [recordingsRes, statsRes, agentsRes, agentStatsRes, dailyRes] = await Promise.all([
        fetch(`${API_URL}/recordings?${params.toString()}`),
        fetch(`${API_URL}/recordings/stats/overview`),
        fetch(`${API_URL}/agents`),
        fetch(`${API_URL}/recordings/stats/agents`),
        fetch(`${API_URL}/recordings/stats/daily`)
      ]);

      const recordingsData = await recordingsRes.json();
      const statsData = await statsRes.json();
      const agentsData = await agentsRes.json();
      const agentStatsData = await agentStatsRes.json();
      const dailyData = await dailyRes.json();

      setRecordings(recordingsData.data || []);
      setRecordingsStats(statsData);
      setAgents(agentsData);
      setAgentStats(agentStatsData);
      setDailyStats(dailyData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, dateTab, sortBy]);

  // Filter recordings by date tab
  const getFilteredRecordingsByDate = (recs) => {
    if (dateTab === 'all') return recs;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    return recs.filter(r => {
      const recDate = new Date(r.call_date);
      const recDateOnly = new Date(recDate.getFullYear(), recDate.getMonth(), recDate.getDate());

      switch (dateTab) {
        case 'today':
          return recDateOnly.getTime() === today.getTime();
        case 'yesterday':
          return recDateOnly.getTime() === yesterday.getTime();
        case 'week':
          return recDateOnly >= weekAgo;
        case 'month':
          return recDateOnly >= monthAgo;
        default:
          return true;
      }
    });
  };

  // Sort recordings
  const getSortedRecordings = (recs) => {
    const sorted = [...recs];
    switch (sortBy) {
      case 'score_desc':
        return sorted.sort((a, b) => (b.overall_score || 0) - (a.overall_score || 0));
      case 'score_asc':
        return sorted.sort((a, b) => (a.overall_score || 0) - (b.overall_score || 0));
      case 'date':
      default:
        return sorted.sort((a, b) => {
          const dateA = new Date(`${a.call_date}T${a.call_time || '00:00:00'}`);
          const dateB = new Date(`${b.call_date}T${b.call_time || '00:00:00'}`);
          return dateB - dateA;
        });
    }
  };

  // Apply date filter and sorting
  const processedRecordings = getSortedRecordings(getFilteredRecordingsByDate(recordings));

  const fetchRecordingDetails = async (recordingId) => {
    try {
      const res = await fetch(`${API_URL}/recordings/${recordingId}`);
      const data = await res.json();
      setRecordingDetails(data);
      return data;
    } catch (error) {
      console.error('Error fetching recording details:', error);
      return null;
    }
  };

  const handleRecordingSelect = (recording) => {
    setSelectedRecording(recording);
    fetchRecordingDetails(recording.recording_id);
  };

  const handleBackToList = () => {
    setSelectedRecording(null);
    setRecordingDetails(null);
  };

  const handlePlayRecording = async (recording) => {
    // Fetch full details to get utterances
    const details = await fetchRecordingDetails(recording.recording_id);
    if (details) {
      const utterances = details.utterances
        ? (typeof details.utterances === 'string' ? JSON.parse(details.utterances) : details.utterances)
        : [];
      setFloatingPlayerRecording(details);
      setFloatingPlayerUtterances(utterances);
    }
  };

  const handleCloseFloatingPlayer = () => {
    setFloatingPlayerRecording(null);
    setFloatingPlayerUtterances([]);
    setFloatingPlayerTime(0);
    setFloatingPlayerSeekTo(null);
  };

  const handleSeekFloatingPlayer = (timeMs) => {
    setFloatingPlayerSeekTo(timeMs);
    // Reset after a short delay to allow multiple seeks to same time
    setTimeout(() => setFloatingPlayerSeekTo(null), 100);
  };

  // Start playback from a specific time (when clicking transcript)
  const handleStartPlaybackFromTime = async (recording, startTimeMs) => {
    const details = await fetchRecordingDetails(recording.recording_id);
    if (details) {
      const utterances = details.utterances
        ? (typeof details.utterances === 'string' ? JSON.parse(details.utterances) : details.utterances)
        : [];
      setFloatingPlayerRecording(details);
      setFloatingPlayerUtterances(utterances);
      // Set seek time after a short delay to ensure player is mounted
      setTimeout(() => {
        setFloatingPlayerSeekTo(startTimeMs);
        setTimeout(() => setFloatingPlayerSeekTo(null), 100);
      }, 100);
    }
  };

  const handleReviewUpdate = async (status) => {
    if (!selectedRecording) return;

    try {
      await fetch(`${API_URL}/recordings/${selectedRecording.recording_id}/review`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          review_status: status,
          reviewed_by: 'admin'
        })
      });

      fetchData();
      fetchRecordingDetails(selectedRecording.recording_id);
    } catch (error) {
      console.error('Error updating review status:', error);
    }
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    setSidebarOpen(false);
    setSelectedRecording(null);
    setRecordingDetails(null);
  };

  const toggleTheme = () => {
    setTheme(t => t === 'light' ? 'dark' : 'light');
  };

  // Render Recordings Section
  const renderRecordingsSection = () => {
    if (selectedRecording && recordingDetails) {
      return (
        <RecordingDetailView
          recording={recordingDetails}
          onBack={handleBackToList}
          onReviewUpdate={handleReviewUpdate}
          floatingPlayerRecording={floatingPlayerRecording}
          floatingPlayerTime={floatingPlayerTime}
          onSeekFloatingPlayer={handleSeekFloatingPlayer}
          onStartPlayback={handleStartPlaybackFromTime}
        />
      );
    }

    return (
      <div className="section-recordings">
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-icon">{Icons.phone}</div>
            <div className="stat-content">
              <span className="stat-value">{recordingsStats?.total_recordings || 0}</span>
              <span className="stat-label">Wszystkie nagrania</span>
            </div>
          </div>
          <div className="stat-card accent">
            <div className="stat-icon">{Icons.award}</div>
            <div className="stat-content">
              <span className="stat-value">{recordingsStats?.avg_score || 0}</span>
              <span className="stat-label">Średnia ocena</span>
            </div>
          </div>
          <div className="stat-card warning">
            <div className="stat-icon">{Icons.alert}</div>
            <div className="stat-content">
              <span className="stat-value">{recordingsStats?.needs_review || 0}</span>
              <span className="stat-label">Do przeglądu</span>
            </div>
          </div>
          <div className="stat-card success">
            <div className="stat-icon">{Icons.check}</div>
            <div className="stat-content">
              <span className="stat-value">{recordingsStats?.high_quality || 0}</span>
              <span className="stat-label">Wysoka jakość</span>
            </div>
          </div>
        </div>

        {/* Date Tabs */}
        <div className="date-tabs">
          <button
            className={`date-tab ${dateTab === 'all' ? 'active' : ''}`}
            onClick={() => setDateTab('all')}
          >
            Wszystkie
          </button>
          <button
            className={`date-tab ${dateTab === 'today' ? 'active' : ''}`}
            onClick={() => setDateTab('today')}
          >
            Dzisiaj
          </button>
          <button
            className={`date-tab ${dateTab === 'yesterday' ? 'active' : ''}`}
            onClick={() => setDateTab('yesterday')}
          >
            Wczoraj
          </button>
          <button
            className={`date-tab ${dateTab === 'week' ? 'active' : ''}`}
            onClick={() => setDateTab('week')}
          >
            Tydzień
          </button>
          <button
            className={`date-tab ${dateTab === 'month' ? 'active' : ''}`}
            onClick={() => setDateTab('month')}
          >
            Miesiąc
          </button>
        </div>

        <div className="filters-bar">
          {/* Review Status Filters */}
          <div className="review-status-filters">
            <button
              className={`status-filter-btn ${filters.review_status === '' && !filters.requires_review ? 'active' : ''}`}
              onClick={() => setFilters(f => ({ ...f, review_status: '', requires_review: false }))}
            >
              Wszystkie
            </button>
            <button
              className={`status-filter-btn pending ${filters.review_status === 'pending' ? 'active' : ''}`}
              onClick={() => setFilters(f => ({ ...f, review_status: 'pending', requires_review: false }))}
            >
              {Icons.alert} Oczekujące
            </button>
            <button
              className={`status-filter-btn approved ${filters.review_status === 'approved' ? 'active' : ''}`}
              onClick={() => setFilters(f => ({ ...f, review_status: 'approved', requires_review: false }))}
            >
              {Icons.check} Zatwierdzone
            </button>
            <button
              className={`status-filter-btn rejected ${filters.review_status === 'rejected' ? 'active' : ''}`}
              onClick={() => setFilters(f => ({ ...f, review_status: 'rejected', requires_review: false }))}
            >
              {Icons.close} Odrzucone
            </button>
            <button
              className={`status-filter-btn training ${filters.review_status === 'needs_training' ? 'active' : ''}`}
              onClick={() => setFilters(f => ({ ...f, review_status: 'needs_training', requires_review: false }))}
            >
              {Icons.user} Szkolenia
            </button>
          </div>

          <div className="filters-row">
            <CustomSelect
              value={filters.agent_id}
              onChange={(val) => setFilters(f => ({ ...f, agent_id: val }))}
              placeholder="Wszyscy agenci"
              options={[
                { value: '', label: 'Wszyscy agenci' },
                ...agents.map(agent => ({ value: agent, label: agent }))
              ]}
            />

            <CustomSelect
              value={sortBy}
              onChange={(val) => setSortBy(val)}
              placeholder="Sortuj"
              options={[
                { value: 'date', label: 'Najnowsze' },
                { value: 'score_desc', label: 'Jakość: Najwyższa' },
                { value: 'score_asc', label: 'Jakość: Najniższa' }
              ]}
            />

            <div className="filter-range">
              <input
                type="number"
                placeholder="Min"
                value={filters.min_score}
                onChange={(e) => setFilters(f => ({ ...f, min_score: e.target.value }))}
                min="0"
                max="100"
              />
              <span>—</span>
              <input
                type="number"
                placeholder="Max"
                value={filters.max_score}
                onChange={(e) => setFilters(f => ({ ...f, max_score: e.target.value }))}
                min="0"
                max="100"
              />
            </div>
          </div>
        </div>

        <div className="recordings-list-container">
          <div className="recordings-list-header">
            <h3>Nagrania ({processedRecordings.length})</h3>
            {processedRecordings.length > recordingsPerPage && (
              <div className="pagination-info">
                Strona {currentPage} z {Math.ceil(processedRecordings.length / recordingsPerPage)}
              </div>
            )}
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <span>Ładowanie...</span>
            </div>
          ) : processedRecordings.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">{Icons.recordings}</div>
              <h4>Brak nagrań</h4>
              <p>Nie znaleziono nagrań spełniających kryteria</p>
            </div>
          ) : (
            <>
              <div className="recordings-list">
                {processedRecordings
                  .slice((currentPage - 1) * recordingsPerPage, currentPage * recordingsPerPage)
                  .map((rec) => (
                    <RecordingRow
                      key={rec.recording_id}
                      recording={rec}
                      onClick={() => handleRecordingSelect(rec)}
                      onPlayClick={handlePlayRecording}
                    />
                  ))}
              </div>

              {processedRecordings.length > recordingsPerPage && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="11 17 6 12 11 7"/>
                      <polyline points="18 17 13 12 18 7"/>
                    </svg>
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15 18 9 12 15 6"/>
                    </svg>
                  </button>

                  <div className="pagination-pages">
                    {(() => {
                      const totalPages = Math.ceil(processedRecordings.length / recordingsPerPage);
                      const pages = [];
                      let startPage = Math.max(1, currentPage - 2);
                      let endPage = Math.min(totalPages, currentPage + 2);

                      if (currentPage <= 3) {
                        endPage = Math.min(5, totalPages);
                      }
                      if (currentPage >= totalPages - 2) {
                        startPage = Math.max(1, totalPages - 4);
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            className={`pagination-page ${currentPage === i ? 'active' : ''}`}
                            onClick={() => setCurrentPage(i)}
                          >
                            {i}
                          </button>
                        );
                      }
                      return pages;
                    })()}
                  </div>

                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(processedRecordings.length / recordingsPerPage), p + 1))}
                    disabled={currentPage === Math.ceil(processedRecordings.length / recordingsPerPage)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(Math.ceil(processedRecordings.length / recordingsPerPage))}
                    disabled={currentPage === Math.ceil(processedRecordings.length / recordingsPerPage)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="13 17 18 12 13 7"/>
                      <polyline points="6 17 11 12 6 7"/>
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Render Stats Section
  const renderStatsSection = () => (
    <div className="section-stats">
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-icon">{Icons.phone}</div>
          <div className="stat-content">
            <span className="stat-value">{recordingsStats?.total_recordings || 0}</span>
            <span className="stat-label">Wszystkie nagrania</span>
          </div>
        </div>
        <div className="stat-card accent">
          <div className="stat-icon">{Icons.award}</div>
          <div className="stat-content">
            <span className="stat-value">{recordingsStats?.avg_score || 0}/100</span>
            <span className="stat-label">Średnia ocena</span>
          </div>
        </div>
        <div className="stat-card success">
          <div className="stat-icon">{Icons.check}</div>
          <div className="stat-content">
            <span className="stat-value">{recordingsStats?.high_quality || 0}</span>
            <span className="stat-label">Ocena 80+</span>
          </div>
        </div>
        <div className="stat-card danger">
          <div className="stat-icon">{Icons.alert}</div>
          <div className="stat-content">
            <span className="stat-value">{recordingsStats?.low_quality || 0}</span>
            <span className="stat-label">Ocena poniżej 60</span>
          </div>
        </div>
      </div>

      <div className="stats-section">
        <h3>Statystyki agentów <span className="subtitle">ostatnie 30 dni</span></h3>
        {agentStats.length === 0 ? (
          <div className="empty-state small">
            <p>Brak danych o agentach</p>
          </div>
        ) : (
          <div className="agents-grid">
            {agentStats.map(agent => (
              <AgentCard
                key={agent.agent_id}
                agent={agent}
                onClick={() => {
                  setFilters(f => ({ ...f, agent_id: agent.agent_id }));
                  handleSectionChange('recordings');
                }}
              />
            ))}
          </div>
        )}
      </div>

      {dailyStats.length > 0 && (
        <div className="stats-section">
          <h3>Statystyki dzienne</h3>
          <div className="daily-table">
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Nagrania</th>
                  <th>Średnia ocena</th>
                  <th>Do przeglądu</th>
                </tr>
              </thead>
              <tbody>
                {dailyStats.slice(0, 14).map((day, idx) => (
                  <tr key={idx}>
                    <td>{new Date(day.call_date).toLocaleDateString('pl-PL')}</td>
                    <td>{day.total_calls || day.total}</td>
                    <td>
                      <span className={`score-pill ${(day.avg_score || 0) >= 80 ? 'high' : (day.avg_score || 0) >= 60 ? 'medium' : 'low'}`}>
                        {Math.round(day.avg_score || 0)}
                      </span>
                    </td>
                    <td>{day.flagged || day.needs_review || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  // If not authenticated, show login screen
  if (!isAuthenticated) {
    return <PinLoginScreen onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className={`app ${floatingPlayerRecording ? 'has-floating-player' : ''}`}>
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <div className="brand-logo">
            <span className="brand-name">Masuria Med</span>
            <span className="brand-subtitle">System kontroli jakości obsługi pacjenta</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeSection === 'recordings' ? 'active' : ''}`}
            onClick={() => handleSectionChange('recordings')}
          >
            <span className="nav-icon">{Icons.recordings}</span>
            <span>Nagrania</span>
          </button>
          <button
            className={`nav-item ${activeSection === 'stats' ? 'active' : ''}`}
            onClick={() => handleSectionChange('stats')}
          >
            <span className="nav-icon">{Icons.stats}</span>
            <span>Statystyki</span>
          </button>
          <button
            className={`nav-item ${activeSection === 'reports' ? 'active' : ''}`}
            onClick={() => handleSectionChange('reports')}
          >
            <span className="nav-icon">{Icons.reports}</span>
            <span>Raporty</span>
          </button>
          <button
            className={`nav-item ${activeSection === 'sales' ? 'active' : ''}`}
            onClick={() => handleSectionChange('sales')}
          >
            <span className="nav-icon">{Icons.sales}</span>
            <span>Sprzedaż</span>
          </button>
          <button
            className={`nav-item ${activeSection === 'training' ? 'active' : ''}`}
            onClick={() => handleSectionChange('training')}
          >
            <span className="nav-icon">{Icons.training}</span>
            <span>Szkolenia</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <button className="theme-btn" onClick={toggleTheme}>
            {theme === 'light' ? Icons.moon : Icons.sun}
            <span>{theme === 'light' ? 'Tryb ciemny' : 'Tryb jasny'}</span>
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="overlay" onClick={() => setSidebarOpen(false)} />}

      <main className="main">
        <header className="header">
          <button className="menu-btn" onClick={() => setSidebarOpen(true)}>
            {Icons.menu}
          </button>
          <div className="header-title">
            <h1>
              {activeSection === 'recordings' && 'Nagrania'}
              {activeSection === 'stats' && 'Statystyki'}
              {activeSection === 'reports' && 'Raporty'}
              {activeSection === 'sales' && 'Sprzedaż'}
              {activeSection === 'training' && 'Szkolenia'}
            </h1>
            <span>Gabinet Stomatologiczny Masuria Med</span>
          </div>
          <button className="theme-btn-mobile" onClick={toggleTheme}>
            {theme === 'light' ? Icons.moon : Icons.sun}
          </button>
        </header>

        <div className="content">
          {activeSection === 'recordings' && renderRecordingsSection()}
          {activeSection === 'stats' && renderStatsSection()}
          {activeSection === 'reports' && (
            <ReportsSection
              recordings={recordings}
              agentStats={agentStats}
              dailyStats={dailyStats}
            />
          )}
          {activeSection === 'sales' && <SalesSection />}
          {activeSection === 'training' && <TrainingSection recordings={recordings} />}
        </div>
      </main>

      {floatingPlayerRecording && (
        <FloatingAudioPlayer
          recording={floatingPlayerRecording}
          utterances={floatingPlayerUtterances}
          onClose={handleCloseFloatingPlayer}
          onTimeUpdate={setFloatingPlayerTime}
          currentTime={floatingPlayerTime}
          seekToTime={floatingPlayerSeekTo}
        />
      )}
    </div>
  );
}

export default App;
